/**
 * PlanItem.kind enum lock (LOCKED — P4.9, DESIGN-SYNTHESIS-v4-FINAL.md §8).
 *
 * Allowed values: 'waypoint' | 'operation_ref' — see ALLOWED_PLAN_ITEM_KINDS.
 *
 * DO NOT ADD 'pre_arrival' or any pre-arrival variant. FT-285 consolidated
 * pre-arrival under the FAB Arrive button as the single regulatory entry
 * point. Adding pre-arrival as a plan item type would:
 *   (a) regress FT-285's consolidation
 *   (b) create duplicate-path audit risk (which entry point fired?)
 *   (c) require Master to disambiguate two pre-arrival UIs
 *
 * Pre-arrival is FOREVER on the FAB Arrive button. If a future ticket needs
 * to associate plan items with arrival, define a NEW kind for that intent
 * (e.g. 'arrival_log_note') — never reach for 'pre_arrival'.
 *
 * Three layers of defense:
 *   1A — WRITE assertion: throws on bad kind at addItem/updateItem (§3.3)
 *   1B — READ filter: drops bad items with console.warn at normalisePlanItems (§3.5)
 *   2  — This comment block.
 *
 * Layer 3 (ESLint custom rule) is NOT ADOPTED in v1 — justified §4.3 of A1 brief.
 */

/**
 * useVoyagePlans — per-vessel Voyage Plan library CRUD.
 *
 * Storage: nested inside voyage_settings.voyagePlans[]
 *          (NOT vessel.voyagePlans — see DESIGN-SYNTHESIS-v4-FINAL.md §3.1 + CODING-BRIEF-A1.md §1.5)
 * Sync:    rides existing voyage_settings sync path (sr.voyage_settings.v1, moduleMap.js:111).
 * Scope:   per-vessel (useStorageState vessel-scopes 'voyage_settings' automatically at useStorageState.js:111).
 *
 * Storage key string 'voyage_settings' is used here AND at VoyageSettingsModal.js:42
 * AND at useVoyages.js:187. If you rename, rename ALL THREE.
 *
 * TODO(post-A1): hoist 'voyage_settings' to OPERATIONS_STORAGE_KEYS — see CODING-BRIEF-A1.md §3.6.
 *
 * PlanItem.kind is LOCKED to 'waypoint' | 'operation_ref' (P4.9).
 * See ALLOWED_PLAN_ITEM_KINDS below and the comment block above (kind enum lock).
 * Pre-arrival is FOREVER excluded — FT-285 consolidated pre-arrival under FAB Arrive.
 *
 * Order semantics (LOCKED A1 §2.5):
 *   items[] array position is canonical.
 *   `order` field is a denormalised hint, re-stamped to match array index on every read
 *   and on every mutation. Invariant: items[i].order === i after any successful CRUD.
 *
 * @typedef {Object} VoyagePlan
 * @property {string} id
 * @property {string} name
 * @property {PlanItem[]} items
 * @property {string} [defaultFrom]
 * @property {string} [defaultTo]
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} [createdBy]
 * @property {boolean} isArchived
 * @property {number} version
 *
 * @typedef {WaypointItem|OperationRef} PlanItem
 *
 * @typedef {Object} WaypointItem
 * @property {string} id
 * @property {'waypoint'} kind
 * @property {string} label
 * @property {string} [instructionNote]
 * @property {number} order
 *
 * @typedef {Object} OperationRef
 * @property {string} id
 * @property {'operation_ref'} kind
 * @property {string} operationId
 * @property {string} [displayLabel]
 * @property {number} order
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStorageState } from '../../useStorageState';

// --- Module-scope constants & helpers (declared BEFORE the hook to avoid TDZ) ---

/**
 * P4.9 enum lock. Pre-arrival is FOREVER excluded — see top-of-file block.
 */
const ALLOWED_PLAN_ITEM_KINDS = Object.freeze(['waypoint', 'operation_ref']);

const generatePlanId = () =>
  `vp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const generateItemId = () =>
  `pi-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Synchronous WRITE-side assertion. Throws on bad kind.
 * Callers that may pass user-provided `kind` MUST wrap in try/catch or
 * upstream-validate against ALLOWED_PLAN_ITEM_KINDS.
 */
function assertValidPlanItemKind(kind) {
  if (!ALLOWED_PLAN_ITEM_KINDS.includes(kind)) {
    throw new Error(
      `[useVoyagePlans] Invalid PlanItem.kind="${kind}". ` +
      `Allowed: ${ALLOWED_PLAN_ITEM_KINDS.join(', ')}. ` +
      `See P4.9 in DESIGN-SYNTHESIS-v4-FINAL.md / CODING-BRIEF-A1.md §4.`
    );
  }
}

/**
 * READ-side filter — drops items with invalid kind (defensive against bad
 * cloud / legacy data). P4.9 belt-and-braces: throws on WRITE, drops on READ.
 * Also re-stamps `order` so array index === order (LOCKED §2.5 invariant).
 */
function normalisePlanItems(items) {
  if (!Array.isArray(items)) return [];
  const filtered = items.filter((item, i) => {
    if (!item || typeof item !== 'object') {
      // eslint-disable-next-line no-console
      console.warn('[useVoyagePlans] dropping non-object plan item at index', i, item);
      return false;
    }
    if (!ALLOWED_PLAN_ITEM_KINDS.includes(item.kind)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[useVoyagePlans] dropping plan item with invalid kind:', item.kind,
        'item:', item
      );
      return false;
    }
    return true;
  });
  return filtered.map((it, i) => ({ ...it, order: i }));
}

/**
 * Plan envelope normaliser. Warns and returns null for envelopes without `id`
 * (R1-C5 — consistent with warn-don't-throw policy for items).
 */
function normalisePlan(plan) {
  if (!plan?.id) {
    // eslint-disable-next-line no-console
    console.warn('[useVoyagePlans] dropping plan envelope without id:', plan);
    return null;
  }
  return {
    ...plan,
    name: typeof plan.name === 'string' ? plan.name : 'Untitled plan',
    isArchived: !!plan.isArchived,
    version: typeof plan.version === 'number' ? plan.version : 1,
    items: normalisePlanItems(plan.items),
  };
}

// --- The hook ---

const useVoyagePlans = () => {
  const { value: settings, setValue: setSettings, loaded } =
    useStorageState('voyage_settings', null);

  // Normalised read — defensive against legacy / bad cloud data (see §2.4, §3.5).
  const plans = useMemo(() => {
    if (!settings || !Array.isArray(settings.voyagePlans)) return [];
    return settings.voyagePlans.map(normalisePlan).filter(Boolean);
  }, [settings]);

  // Synchronous mirror of the persisted voyagePlans so item CRUD can report
  // success/failure as a return value. React's useState setter updater is
  // queued (runs later during commit), so we cannot rely on closure-variable
  // assignments inside the updater being visible by the time the caller reads
  // the return value. The ref is updated synchronously alongside every write.
  const persistedPlansRef = useRef(
    Array.isArray(settings?.voyagePlans) ? settings.voyagePlans : []
  );
  // Keep ref in sync with the latest settings on every render.
  // Reassignment during render is intentional and idempotent (strict-mode-safe).
  // Do NOT move into useEffect — that would re-introduce the staleness this ref is designed to eliminate.
  persistedPlansRef.current = Array.isArray(settings?.voyagePlans)
    ? settings.voyagePlans
    : [];

  // writePlans takes an UPDATER, not a pre-computed array (R1-C2).
  // The updater runs inside setSettings(prev => ...) so it ALWAYS sees the freshest
  // persisted voyagePlans — even if two CRUD calls happen in the same tick.
  // Sibling keys (savedPorts/operationsList/etc.) are preserved via the spread.
  // The ref is updated synchronously alongside the React state queue so item
  // CRUD return-values are reliable even when React defers the state commit.
  //
  // IMPORTANT: The updater is invoked TWICE per writePlans call (once for persistedPlansRef mirror,
  // once inside setSettings). It MUST be a pure function — side effects (console.warn, throw, id generation,
  // timestamp creation) inside the updater will fire twice. Hoist any side effect to the caller.
  const writePlans = useCallback((updater) => {
    const nextPlans = updater(persistedPlansRef.current);
    persistedPlansRef.current = nextPlans;
    setSettings((prev) => {
      const prevPlans = Array.isArray(prev?.voyagePlans) ? prev.voyagePlans : [];
      // Re-run the updater inside the setter to remain robust under React 19
      // strict-mode dual invocation AND to compose safely with any concurrent
      // setSettings call from a sibling consumer (R1-C2 stale-closure fix).
      const nextPlansInner = updater(prevPlans);
      return { ...(prev || {}), voyagePlans: nextPlansInner };
    });
  }, [setSettings]);

  // --- Plan CRUD ---

  const createPlan = useCallback((draft = {}) => {
    const now = new Date().toISOString();
    const plan = {
      id: generatePlanId(),
      name: (draft.name || 'Untitled plan').trim(),
      items: [],
      defaultFrom: draft.defaultFrom || '',
      defaultTo: draft.defaultTo || '',
      createdAt: now,
      updatedAt: now,
      createdBy: draft.createdBy || undefined,
      isArchived: false,
      version: 1,
    };
    writePlans((currentPlans) => [...currentPlans, plan]);
    // Return the full plan (not just id) so callers can chain `addItem(plan.id, ...)`
    // synchronously without re-reading hook state (R1-C2).
    return plan;
  }, [writePlans]);

  const updatePlan = useCallback((id, patch) => {
    const now = new Date().toISOString();
    writePlans((currentPlans) => currentPlans.map((p) => p.id === id
      ? { ...p, ...patch, updatedAt: now, version: (p.version || 1) + 1 }
      : p
    ));
  }, [writePlans]);

  const archivePlan = useCallback((id) =>
    updatePlan(id, { isArchived: true }), [updatePlan]);

  const unarchivePlan = useCallback((id) =>
    updatePlan(id, { isArchived: false }), [updatePlan]);

  const deletePlan = useCallback((id) =>
    writePlans((currentPlans) => currentPlans.filter((p) => p.id !== id)), [writePlans]);

  // --- Item CRUD ---

  const addItem = useCallback((planId, item) => {
    // Synchronous throw on invalid kind (callers must wrap or upstream-validate).
    assertValidPlanItemKind(item?.kind);
    const now = new Date().toISOString();   // hoisted — both updater invocations share this
    // Generate the item id OUTSIDE the writePlans updater so the synchronous
    // return value is reliable. React's useState setter updater is queued
    // (runs later during commit), so closure-variable assignments inside the
    // updater are not visible to the caller of addItem on return.
    const newItem = item.kind === 'waypoint'
      ? {
          id: generateItemId(),
          kind: 'waypoint',
          label: (item.label || '').trim(),
          instructionNote: item.instructionNote || '',
          order: 0, // re-stamped inside the updater
        }
      : {
          id: generateItemId(),
          kind: 'operation_ref',
          operationId: item.operationId,
          displayLabel: item.displayLabel || undefined,
          order: 0, // re-stamped inside the updater
        };
    // NOTE: persistedPlansRef.current is the RAW voyage_settings.voyagePlans (unnormalised).
    // findIndex by plan.id is safe because malformed envelopes have no id (id === undefined never matches).
    // Synchronous plan-exists check via the ref (which is kept in sync with
    // every write — see writePlans above).
    const idxSync = persistedPlansRef.current.findIndex((p) => p.id === planId);
    if (idxSync < 0) return null;
    writePlans((currentPlans) => {
      const idx = currentPlans.findIndex((p) => p.id === planId);
      if (idx < 0) return currentPlans;  // plan not found, no-op (already returned null above)
      const plan = currentPlans[idx];
      const existingItems = Array.isArray(plan.items) ? plan.items : [];
      const stampedItem = { ...newItem, order: existingItems.length };
      const nextPlan = {
        ...plan,
        items: [...existingItems, stampedItem],
        updatedAt: now,
        version: (plan.version || 1) + 1,
      };
      const next = [...currentPlans];
      next[idx] = nextPlan;
      return next;
    });
    return newItem.id;
  }, [writePlans]);

  const updateItem = useCallback((planId, itemId, patch) => {
    // Synchronous throw if patch carries an invalid kind (R1-S2).
    if (patch?.kind) assertValidPlanItemKind(patch.kind);
    const now = new Date().toISOString();   // hoisted — both updater invocations share this
    writePlans((currentPlans) => {
      const idx = currentPlans.findIndex((p) => p.id === planId);
      if (idx < 0) return currentPlans;
      const plan = currentPlans[idx];
      const existingItems = Array.isArray(plan.items) ? plan.items : [];
      const nextItems = existingItems.map((it) => it.id === itemId ? { ...it, ...patch } : it);
      const next = [...currentPlans];
      next[idx] = { ...plan, items: nextItems, updatedAt: now, version: (plan.version || 1) + 1 };
      return next;
    });
  }, [writePlans]);

  const removeItem = useCallback((planId, itemId) => {
    const now = new Date().toISOString();   // hoisted — both updater invocations share this
    writePlans((currentPlans) => {
      const idx = currentPlans.findIndex((p) => p.id === planId);
      if (idx < 0) return currentPlans;
      const plan = currentPlans[idx];
      const existingItems = Array.isArray(plan.items) ? plan.items : [];
      const filtered = existingItems.filter((it) => it.id !== itemId);
      // Re-stamp order so array index === order (LOCKED §2.5 invariant)
      const renumbered = filtered.map((it, i) => ({ ...it, order: i }));
      const next = [...currentPlans];
      next[idx] = { ...plan, items: renumbered, updatedAt: now, version: (plan.version || 1) + 1 };
      return next;
    });
  }, [writePlans]);

  const moveItem = useCallback((planId, itemId, direction /* 'up' | 'down' */) => {
    const now = new Date().toISOString();   // hoisted — both updater invocations share this
    writePlans((currentPlans) => {
      const idx = currentPlans.findIndex((p) => p.id === planId);
      if (idx < 0) return currentPlans;
      const plan = currentPlans[idx];
      const existingItems = Array.isArray(plan.items) ? plan.items : [];
      const itemIdx = existingItems.findIndex((it) => it.id === itemId);
      if (itemIdx < 0) return currentPlans;
      const swapIdx = direction === 'up' ? itemIdx - 1 : itemIdx + 1;
      // Boundary moves are a TRUE zero-side-effect no-op (R1-S1):
      // return the SAME array reference so setSettings does not trigger a re-render
      // and version is NOT bumped.
      if (swapIdx < 0 || swapIdx >= existingItems.length) return currentPlans;
      const nextItems = [...existingItems];
      [nextItems[itemIdx], nextItems[swapIdx]] = [nextItems[swapIdx], nextItems[itemIdx]];
      // Re-stamp order so array index === order (LOCKED §2.5 invariant)
      const renumbered = nextItems.map((it, i) => ({ ...it, order: i }));
      const next = [...currentPlans];
      next[idx] = { ...plan, items: renumbered, updatedAt: now, version: (plan.version || 1) + 1 };
      return next;
    });
  }, [writePlans]);

  // --- Memoised derived views ---

  const activePlans = useMemo(
    () => plans.filter((p) => !p.isArchived),
    [plans]
  );
  const archivedPlans = useMemo(
    () => plans.filter((p) => p.isArchived),
    [plans]
  );

  const api = useMemo(() => ({
    plans,
    activePlans,
    archivedPlans,
    loaded,
    createPlan,
    updatePlan,
    archivePlan,
    unarchivePlan,
    deletePlan,
    addItem,
    updateItem,
    removeItem,
    moveItem,
  }), [
    plans, activePlans, archivedPlans, loaded,
    createPlan, updatePlan, archivePlan, unarchivePlan, deletePlan,
    addItem, updateItem, removeItem, moveItem,
  ]);

  // Dev-helper bridge: when the staging build sets REACT_APP_ENABLE_A1_HELPER=true,
  // attach the live hook API to window.__a1RoundTrip so the advisor's Playwright
  // round-trip script can drive the hook from the browser console.
  // Production builds MUST NOT set this build-arg (§3.10, §10.5).
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      process.env.REACT_APP_ENABLE_A1_HELPER === 'true' &&
      window.__a1RoundTrip &&
      typeof window.__a1RoundTrip._attach === 'function'
    ) {
      window.__a1RoundTrip._attach(api);
    }
  }, [api]);

  return api;
};

// --- Dev helper exposure ---
//
// [R2-C2] Gated on REACT_APP_ENABLE_A1_HELPER (NOT NODE_ENV) so the staging Azure
// build (which runs NODE_ENV=production) can still expose this when the build-arg
// is set. Production builds MUST NOT pass this build-arg.
if (
  typeof window !== 'undefined' &&
  process.env.REACT_APP_ENABLE_A1_HELPER === 'true' &&
  typeof window.__a1RoundTrip === 'undefined'
) {
  // eslint-disable-next-line no-underscore-dangle
  window.__a1RoundTrip = {
    _attach: function attach(liveApi) {
      // Copy live methods + live arrays onto the singleton. The useEffect inside
      // useVoyagePlans re-attaches on every render so `plans` stays fresh.
      Object.assign(window.__a1RoundTrip, liveApi);
    },
    createPlan: () => {
      throw new Error(
        '[useVoyagePlans __a1RoundTrip] _attach() has not been called yet — ' +
        'mount a useVoyagePlans consumer first'
      );
    },
    addItem: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    updateItem: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    removeItem: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    moveItem: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    deletePlan: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    archivePlan: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    unarchivePlan: () => {
      throw new Error('[useVoyagePlans __a1RoundTrip] _attach() has not been called');
    },
    plans: [],
    activePlans: [],
    archivedPlans: [],
    loaded: false,
  };
}

// Exported for tests that need to assert against the enum directly.
export { ALLOWED_PLAN_ITEM_KINDS };

export default useVoyagePlans;
